import { Injectable } from '@angular/core';
import {
    FlyFreelyError,
    FlyFreelyLoggingService,
    PersonRolesDto,
    PersonService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import {
    NgSelectPaginationValues,
    NgSelectSearch,
    ngSelectPreSearchConfigure,
    updateNgSelectData
} from '@flyfreely-portal-ui/ng-select';
import { personSearch } from '@flyfreely-portal-ui/ui';
import {
    BehaviorSubject,
    Subject,
    concatMap,
    debounceTime,
    of,
    takeUntil
} from 'rxjs';
import { PersonWithRegisterStatus } from '../mission-edit-v2/mission-edit.service';

@Injectable()
export class MissionRecordEditDataService {
    organisationId: number;

    personSearch = personSearch.bind(this);

    private personnelSource = new BehaviorSubject<PersonWithRegisterStatus[]>(
        []
    );
    personnel$ = this.personnelSource.asObservable();

    private personSearchSource = new Subject<NgSelectSearch>();
    personSelectConfig: NgSelectPaginationValues = {
        currentPage: 0,
        preSearchPage: null,
        searchValue: null,
        totalItems: null
    };

    private workTracker = new WorkTracker();
    private workingSource = new BehaviorSubject<boolean>(false);
    working$ = this.workingSource.asObservable();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private personService: PersonService,
        private logging: FlyFreelyLoggingService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => this.workingSource.next(working));

        this.personSearchSource
            .pipe(debounceTime(300), takeUntil(this.ngUnsubscribe$))
            .subscribe(search => {
                this.personSelectConfig = ngSelectPreSearchConfigure(
                    search,
                    this.personSelectConfig
                );
                this.refreshPersonList();
            });
    }

    ngOnDestroy() {
        this.personSearchSource.complete();
        this.personnelSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    setParameters(organisationId: number) {
        this.organisationId = organisationId;
        this.resetPersonSelectConfig();
        this.refreshPersonList();
    }

    private refreshPersonList() {
        this.personService
            .findPersonnelGraphQL(
                this.organisationId,
                this.personSelectConfig.currentPage,
                15,
                {
                    // Hard coding the pilot role here because that's all that can be selected for retrospective missions
                    roleOrList: [PersonRolesDto.Roles.PILOT],
                    textSearch: this.personSelectConfig.searchValue || undefined
                }
            )
            .pipe(
                concatMap(result => {
                    this.personSelectConfig = updateNgSelectData(
                        result,
                        this.personSelectConfig
                    );
                    return of(
                        result.results
                            ?.filter(
                                p =>
                                    p != null &&
                                    this.personnel.findIndex(
                                        person => person.id === p.id
                                    ) === -1
                            )
                            .map<PersonWithRegisterStatus>(
                                p =>
                                    ({
                                        id: p.id,
                                        firstName: p.firstName,
                                        lastName: p.lastName,
                                        email: p.email,
                                        phoneNumber: p.phoneNumber,
                                        roles: p.roles,
                                        workgroups: p.workgroupList,
                                        active: p.active,
                                        registerStatuses: null
                                    }) as any
                            )
                            .filter(p => p != null && p.id != null) ?? []
                    );
                }),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: (newPersonnel: PersonWithRegisterStatus[]) => {
                    const updatedPersonnel =
                        this.personnel.concat(newPersonnel);

                    this.personnelSource.next(updatedPersonnel);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading mission personnel: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    onPersonSelectScroll() {
        const resultLength =
            this.personSelectConfig.searchValue == null
                ? this.personnel.length
                : this.personnel.filter(person =>
                      this.personSearch(
                          this.personSelectConfig.searchValue,
                          person
                      )
                  ).length;

        if (
            this.personSelectConfig.totalItems == null ||
            resultLength !== this.personSelectConfig.totalItems
        ) {
            this.refreshPersonList();
        }
    }

    onPersonSelectSearch(search: NgSelectSearch) {
        this.personSearchSource.next(search);
    }

    onPersonSelectClose() {
        this.resetPersonSelectConfig();
        this.refreshPersonList();
    }

    private resetPersonSelectConfig() {
        this.personSelectConfig = {
            ...this.personSelectConfig,
            searchValue: null,
            currentPage: 0,
            preSearchPage: null
        };
    }

    private get personnel() {
        return this.personnelSource.getValue();
    }
}
